import { useParams, useNavigate } from 'react-router-dom'
import { useApplication, useUpdateStatus } from '@/hooks/useApplications'
import { ArrowLeft, ExternalLink, Sparkles, AlertTriangle, MessageSquare, Briefcase, Mail, Phone, CheckCircle2 } from 'lucide-react'

export default function ApplicationDetail() {
    const { appId } = useParams()
    const navigate = useNavigate()
    const { data: app, isLoading } = useApplication(appId!)
    const updateStatus = useUpdateStatus()

    if (isLoading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
    if (!app) return <div style={{ padding: 40 }}>Not found</div>

    return (
        <div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/applications')} style={{ marginBottom: 24, border: 'none' }}>
                <ArrowLeft size={16} /> Back to Applications
            </button>

            {/* Header */}
            <div className="card" style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.5, marginBottom: 8 }}>{app.name}</h1>
                    <div style={{ display: 'flex', gap: 16, color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Briefcase size={14} /> {app.position}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Mail size={14} /> {app.email}</span>
                        {app.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Phone size={14} /> {app.phone}</span>}
                        {app.portfolio && (
                            <a href={app.portfolio} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--accent)', textDecoration: 'none' }}>
                                <ExternalLink size={14} /> Portfolio
                            </a>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>Status:</span>
                    <select
                        className="input"
                        value={app.status}
                        onChange={(e) => updateStatus.mutate({ appId: appId!, status: e.target.value as never })}
                        style={{ width: 140, padding: '8px 12px', background: 'var(--bg)' }}
                        disabled={updateStatus.isPending}
                    >
                        <option value="new">New</option>
                        <option value="reviewed">Reviewed</option>
                        <option value="shortlisted">Shortlisted</option>
                        <option value="interviewing">Interviewing</option>
                        <option value="offered">Offered</option>
                        <option value="hired">Hired</option>
                        <option value="rejected">Rejected</option>
                    </select>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 20, alignItems: 'start' }}>
                {/* Left Col: Resume & Cover Letter */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div className="card">
                        <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Resume Text</h2>
                        <div style={{
                            background: 'var(--bg)', padding: 16, borderRadius: 8, border: '1px solid var(--border)',
                            maxHeight: 600, overflowY: 'auto', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                        }}>
                            {app.resumeMarkdown || app.resumeText || 'No resume text available.'}
                        </div>
                    </div>
                    {app.coverLetter && (
                        <div className="card">
                            <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Cover Letter</h2>
                            <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{app.coverLetter}</div>
                        </div>
                    )}
                </div>

                {/* Right Col: AI Analysis */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'sticky', top: 32 }}>
                    <div className="card" style={{ background: 'linear-gradient(to bottom, #110e1f, var(--bg-card))', borderColor: 'var(--accent-glow)' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                            <div style={{ background: 'var(--accent)', padding: 8, borderRadius: 8, display: 'flex', color: 'white' }}><Sparkles size={16} /></div>
                            <h2 style={{ fontSize: 14, fontWeight: 700 }}>AI Analysis</h2>
                        </div>

                        {!app.aiProcessed ? (
                            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>AI processing in progress...</div>
                        ) : (
                            <>
                                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                                    <div style={{ fontSize: 40, fontWeight: 900, letterSpacing: -2, color: 'var(--text)', lineHeight: 1 }}>{app.aiScore}</div>
                                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-dim)', marginTop: 4 }}>Gemini Score</div>
                                </div>

                                <p style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 20, color: 'var(--text)' }}>
                                    "{app.aiSummary}"
                                </p>

                                {/* Score Breakdown */}
                                {app.aiBreakdown && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
                                        {Object.entries(app.aiBreakdown).map(([key, val]) => (
                                            <div key={key}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                                                    <span style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</span>
                                                    <span style={{ fontWeight: 600 }}>{val}%</span>
                                                </div>
                                                <div className="progress-bar">
                                                    <div className="progress-fill" style={{ width: `${val}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Strengths & Red Flags */}
                                {app.aiStrengths && app.aiStrengths.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Top Strengths</h3>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {app.aiStrengths.map((str, i) => (
                                                <li key={i} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                                    <CheckCircle2 size={14} color="var(--green)" style={{ flexShrink: 0, marginTop: 1 }} />
                                                    {str}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {app.aiRedFlags && app.aiRedFlags.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Red Flags</h3>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {app.aiRedFlags.map((flag, i) => (
                                                <li key={i} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start', color: '#ff8a8a' }}>
                                                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                                    {flag}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Interview Questions */}
                                {app.aiQuestions && app.aiQuestions.length > 0 && (
                                    <div>
                                        <h3 style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Suggested Qs</h3>
                                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {app.aiQuestions.map((q, i) => (
                                                <li key={i} style={{ fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                                    <MessageSquare size={14} color="var(--text-dim)" style={{ flexShrink: 0, marginTop: 1 }} />
                                                    {q}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
